import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import { PRODUCTS } from '../data/products';
import { capturesPanelCount } from '../utils/panelCount';
import { PRODUCT_META } from '../data/productMeta';
import { snapDepositPercent } from '../utils/depositOptions';
import { computeQuoteSummary } from '../utils/quoteSummaryMath';
import type { CustomerInfo, AddonItem, LineItem } from '../types/quote';

/**
 * The quote workspace types now live with the other shared entity types
 * (`src/types/quote.ts`, backlog #113) — the calculator owns the behaviour,
 * not the shape. Re-exported here so every historical import path keeps
 * working; new code imports them from `src/types`.
 */
export type { CustomerInfo, AddonItem, LineItem } from '../types/quote';

/** Subset of QuotePayload that loadQuote actually reads. Declared locally
 * rather than imported from `quoteService`, which pulls in the Firebase SDK
 * and the whole save/send surface for what the load path reads as plain data.
 * If the load path needs more fields later, widen this shape rather than
 * reaching for `any`. */
export interface LoadQuotePayload {
  id: string;
  status: string;
  customer: CustomerInfo;
  cart: LineItem[];
  issuedCompany: string;
  depositPercent?: number;
  interiorDesign?: boolean;
  /** Free-text remark applying to the WHOLE quotation/order (vs the per-line
   *  `remarks` on a LineItem). Customer-facing: printed on the quotation and
   *  carried to the OC + Final Invoice. Optional — legacy quotes lack it. */
  orderRemarks?: string;
  /** PepperCloud opportunity id this quote belongs to. Set by the rep at
   *  checkout; drives the contract-value write-back on confirmation. */
  pepperCloudOpportunityId?: string;
  quoteNumber?: string;
  totals?: {
    itemsTotal?: number;
    deliveryFee?: number;
    cbdSurchargeFee?: number;
    appliedDiscount?: number;
  };
  metadata?: {
    version?: number;
    rootQuoteId?: string;
    /** Original creator/author, carried into the workspace so a re-save keeps
     *  the quote owned by the rep who created it (never reassigned to an admin
     *  who edits it). See resolveQuoteAuthor in useQuoteCheckout. */
    createdBy?: string;
    /** The quote's true creation date, carried into the workspace so an edit
     *  re-saves it rather than stamping "now" (backlog #54). */
    createdAt?: string | null;
    authorName?: string;
    authorMobile?: string;
  };
}

interface QuoteState {
  activeQuoteId: string | null;
  activeQuoteStatus: string | null;
  activeQuoteVersion: number;
  activeRootQuoteId: string | null;
  /** Human-readable quote number — `QTE-2026-0042`. Carried across versions
   * (a new revision of an existing quote keeps the same number) and across
   * DRAFT rehydrate → re-save cycles. Null for brand-new workspaces and for
   * legacy quotes saved before the field landed. See QuotePayload.quoteNumber. */
  activeQuoteNumber: string | null;
  /** Original creator/author of the loaded quote. Null for a brand-new
   *  workspace. Carried so executeSave preserves ownership on re-save/fork
   *  instead of stamping the current user (resolveQuoteAuthor). */
  activeQuoteCreatedBy: string | null;
  /** The loaded quote's ORIGINAL `metadata.createdAt`, carried so an edit
   *  re-saves it instead of letting `saveQuote`'s `?? now` fallback overwrite
   *  the quote's true creation date (backlog #54). Null for a brand-new
   *  workspace, and deliberately null for a forked NEW VERSION — that is a new
   *  document and gets its own creation date. */
  activeQuoteCreatedAt: string | null;
  activeQuoteAuthorName: string | null;
  activeQuoteAuthorMobile: string | null;
  customer: CustomerInfo;
  items: LineItem[];
  cbdSurcharge: boolean;
  deliveryWaived: boolean;
  lumpSumDiscount: number;
  issuedCompany: string;
  /** Deposit asked of the customer, as a percentage (10–100). undefined = the
   * payment panel asks for the full grand total — which is not the same as the
   * confirmation gate requiring it. See QuotePayload.depositPercent. */
  depositPercent: number | undefined;
  /** Interior Design order — see QuotePayload.interiorDesign. */
  interiorDesign: boolean;
  /** Free-text remark applying to the whole quotation/order. Always a string in
   *  the workspace ('' = none); persisted as undefined when blank. */
  orderRemarks: string;
  /** '' = not linked. */
  pepperCloudOpportunityId: string;

  // Actions
  setCustomer: (customer: CustomerInfo) => void;
  updateCustomerField: <K extends keyof CustomerInfo>(field: K, value: CustomerInfo[K]) => void;
  setCbdSurcharge: (val: boolean) => void;
  setDeliveryWaived: (val: boolean) => void;
  setLumpSumDiscount: (val: number) => void;
  setIssuedCompany: (company: string) => void;
  setDepositPercent: (val: number | undefined) => void;
  setInteriorDesign: (val: boolean) => void;
  setOrderRemarks: (val: string) => void;
  setPepperCloudOpportunityId: (val: string) => void;
  addItem: () => void;
  addAdhocItem: () => void;
  duplicateItem: (id: string) => void;
  removeItem: (id: string) => void;
  /** Swap an item with its neighbour. No-op at the ends and for an unknown id. */
  moveItem: (id: string, direction: 'up' | 'down') => void;
  updateItem: <K extends keyof LineItem>(id: string, field: K, value: LineItem[K]) => void;
  addAddon: (itemId: string, prdCode: string) => void;
  removeAddon: (itemId: string, addonId: string) => void;
  updateAddon: <K extends keyof AddonItem>(
    itemId: string,
    addonId: string,
    field: K,
    value: AddonItem[K],
  ) => void;
  loadQuote: (payload: LoadQuotePayload) => void;
  /** Seed the workspace from an order revision for a Phase 2c variation.
   * Like loadQuote it derives the cbd/delivery/discount flags from totals, but
   * it carries NO quote identity — every activeQuote* field is cleared so a
   * variation can never be saved as a quote from the editor. */
  loadVariationDraft: (input: {
    items: LineItem[];
    totals: {
      itemsTotal?: number;
      deliveryFee?: number;
      cbdSurchargeFee?: number;
      appliedDiscount?: number;
    };
    issuedCompany: string;
  }) => void;
  resetWorkspace: () => void;
}

export const useQuoteStore = create<QuoteState>()(
  persist(
    immer((set) => ({
      activeQuoteId: null,
      activeQuoteStatus: null,
      activeQuoteVersion: 1,
      activeRootQuoteId: null,
      activeQuoteNumber: null,
      activeQuoteCreatedBy: null,
      activeQuoteCreatedAt: null,
      activeQuoteAuthorName: null,
      activeQuoteAuthorMobile: null,
      customer: {
        name: '',
        mobile: '',
        unit: '',
        postal: '',
        blk: '',
        street: '',
        building: '',
        preparedBy: '',
        hasDifferentInstall: false,
        installPostal: '',
        installBlk: '',
        installStreet: '',
        installBuilding: '',
        installUnit: '',
        installName: '',
        installMobile: '',
      },
      items: [],
      cbdSurcharge: false,
      deliveryWaived: false,
      lumpSumDiscount: 0,
      issuedCompany: 'WSS Windows & Doors',
      depositPercent: 50,
      interiorDesign: false,
      orderRemarks: '',
      pepperCloudOpportunityId: '',

      setCustomer: (customer) =>
        set((state) => {
          state.customer = customer;
        }),
      updateCustomerField: (field, value) =>
        set((state) => {
          state.customer[field] = value;
        }),
      setCbdSurcharge: (val) =>
        set((state) => {
          state.cbdSurcharge = val;
        }),
      setDeliveryWaived: (val) =>
        set((state) => {
          state.deliveryWaived = val;
        }),
      setLumpSumDiscount: (val) =>
        set((state) => {
          state.lumpSumDiscount = val;
        }),
      setIssuedCompany: (company) =>
        set((state) => {
          state.issuedCompany = company;
        }),
      setDepositPercent: (val) =>
        set((state) => {
          state.depositPercent = val;
        }),
      setInteriorDesign: (val) =>
        set((state) => {
          state.interiorDesign = val;
        }),
      setOrderRemarks: (val) =>
        set((state) => {
          state.orderRemarks = val;
        }),
      setPepperCloudOpportunityId: (val) =>
        set((state) => {
          state.pepperCloudOpportunityId = val;
        }),

      loadQuote: (payload) =>
        set((state) => {
          state.activeQuoteId = payload.id;
          state.activeQuoteStatus = payload.status;
          state.activeQuoteVersion = payload.metadata?.version || 1;
          state.activeRootQuoteId = payload.metadata?.rootQuoteId || payload.id;
          state.activeQuoteNumber = payload.quoteNumber ?? null;
          state.activeQuoteCreatedBy = payload.metadata?.createdBy ?? null;
          state.activeQuoteCreatedAt = payload.metadata?.createdAt ?? null;
          state.activeQuoteAuthorName = payload.metadata?.authorName ?? null;
          state.activeQuoteAuthorMobile = payload.metadata?.authorMobile ?? null;
          state.customer = payload.customer;
          state.items = payload.cart;
          state.issuedCompany = payload.issuedCompany;
          state.cbdSurcharge = (payload.totals?.cbdSurchargeFee || 0) > 0;
          state.deliveryWaived =
            payload.totals?.deliveryFee === 0 &&
            (payload.totals?.itemsTotal || 0) > 0 &&
            !state.cbdSurcharge;
          state.lumpSumDiscount = payload.totals?.appliedDiscount || 0;
          // Snap on load so the stored value matches what the checkout dropdown
          // shows (10–100% in tens). A non-multiple of 10 would otherwise leave
          // the store holding e.g. 33 while the `<select>` renders blank.
          // Since the picker widened to a 10% floor this no longer RAISES a
          // legacy sub-50% deposit on load — a quote stored at 20% now displays
          // and re-saves 20, which is what every server gate already used.
          // `null`/absent = full payment, kept as undefined.
          state.depositPercent =
            payload.depositPercent == null ? undefined : snapDepositPercent(payload.depositPercent);
          state.interiorDesign = payload.interiorDesign === true;
          state.orderRemarks = payload.orderRemarks ?? '';
          state.pepperCloudOpportunityId = payload.pepperCloudOpportunityId ?? '';
        }),

      loadVariationDraft: (input) =>
        set((state) => {
          // No quote identity — a variation saves to the OC, never a quote.
          state.activeQuoteId = null;
          state.activeQuoteStatus = null;
          state.activeQuoteVersion = 1;
          state.activeRootQuoteId = null;
          state.activeQuoteNumber = null;
          state.activeQuoteCreatedBy = null;
          state.activeQuoteCreatedAt = null;
          state.activeQuoteAuthorName = null;
          state.activeQuoteAuthorMobile = null;
          // A variation carries no quote identity, so it must not inherit a
          // stale remark from the workspace it was drafted from.
          state.orderRemarks = '';
          // A variation carries no quote identity; inheriting a stale
          // opportunity id would write this order's value onto another deal.
          state.pepperCloudOpportunityId = '';
          state.items = input.items;
          state.issuedCompany = input.issuedCompany;
          // Mirror loadQuote's flag derivation so the two can't drift.
          state.cbdSurcharge = (input.totals.cbdSurchargeFee || 0) > 0;
          state.deliveryWaived =
            input.totals.deliveryFee === 0 &&
            (input.totals.itemsTotal || 0) > 0 &&
            !state.cbdSurcharge;
          state.lumpSumDiscount = input.totals.appliedDiscount || 0;
        }),

      resetWorkspace: () =>
        set((state) => {
          state.activeQuoteId = null;
          state.activeQuoteStatus = null;
          state.activeQuoteVersion = 1;
          state.activeRootQuoteId = null;
          state.activeQuoteNumber = null;
          state.activeQuoteCreatedBy = null;
          state.activeQuoteCreatedAt = null;
          state.activeQuoteAuthorName = null;
          state.activeQuoteAuthorMobile = null;
          state.customer = {
            name: '',
            mobile: '',
            email: '',
            unit: '',
            postal: '',
            blk: '',
            street: '',
            building: '',
            preparedBy: '',
            hasDifferentInstall: false,
            installPostal: '',
            installBlk: '',
            installStreet: '',
            installBuilding: '',
            installUnit: '',
            installName: '',
            installMobile: '',
          };
          state.items = [];
          state.cbdSurcharge = false;
          state.deliveryWaived = false;
          state.lumpSumDiscount = 0;
          state.issuedCompany = 'WSS Windows & Doors';
          state.depositPercent = 50;
          state.interiorDesign = false;
          state.orderRemarks = '';
          state.pepperCloudOpportunityId = '';
        }),

      addItem: () =>
        set((state) => {
          state.items.push({
            id: crypto.randomUUID(),
            source: 'catalog',
            cat1: '',
            cat2: '',
            product: null,
            qty: 1,
            widthMm: '',
            heightMm: '',
            room: '',
            otherRoom: '',
            addons: [],
          });
        }),

      addAdhocItem: () =>
        set((state) => {
          state.items.push({
            id: crypto.randomUUID(),
            source: 'adhoc',
            cat1: '',
            cat2: '',
            product: null,
            qty: 1,
            widthMm: '',
            heightMm: '',
            room: '',
            otherRoom: '',
            adhocDescription: '',
            adhocUnitPrice: '',
            adhocUom: 'each',
            vendorId: '',
            vendorName: '',
            purchaseCost: '',
            addons: [],
          });
        }),

      duplicateItem: (id) =>
        set((state) => {
          const item = state.items.find((i) => i.id === id);
          if (item) {
            const clonedItem: LineItem = {
              ...item,
              id: crypto.randomUUID(),
              addons: item.addons.map((a) => ({
                ...a,
                id: crypto.randomUUID(),
              })),
            };
            state.items.push(clonedItem);
          }
        }),

      removeItem: (id) =>
        set((state) => {
          const idx = state.items.findIndex((i) => i.id === id);
          if (idx !== -1) state.items.splice(idx, 1);
        }),

      moveItem: (id, direction) =>
        set((state) => {
          const idx = state.items.findIndex((i) => i.id === id);
          if (idx === -1) return;
          const target = direction === 'up' ? idx - 1 : idx + 1;
          if (target < 0 || target >= state.items.length) return;
          // Order is all that changes: the saved cart, the OC revision snapshot
          // and every printed document follow this array as-is.
          const [moved] = state.items.splice(idx, 1);
          state.items.splice(target, 0, moved);
        }),

      updateItem: (id, field, value) =>
        set((state) => {
          const item = state.items.find((i) => i.id === id);
          if (item) {
            item[field] = value;
            // Clear dependent addons + colour selection if primary product
            // changes (the new product may not offer the chosen colour).
            if (field === 'product') {
              item.addons = [];
              item.colour = undefined;
              // A panel count left over from the previous product would keep
              // PRINTING on the customer's quotation while its input is
              // hidden — the rep could neither see nor clear it (caught in the
              // emulator, 2026-09-15). Kept across an eligible→eligible swap
              // (e.g. 2-way to 3-way sliding), where it is still meaningful.
              if (!capturesPanelCount(item.product?.prd_code)) {
                item.panelCount = '';
              }
            }
          }
        }),

      addAddon: (itemId, prdCode) =>
        set((state) => {
          if (!prdCode) return;
          const addProduct = PRODUCTS.find((p) => p.prd_code === prdCode);
          if (!addProduct) return;
          const meta = PRODUCT_META[prdCode] || {};

          const item = state.items.find((i) => i.id === itemId);
          if (item) {
            item.addons.push({
              id: crypto.randomUUID(),
              product: addProduct,
              qty: item.qty,
              widthMm: '',
              heightMm: '',
              inheritDims: !!meta.inheritDims,
            });
          }
        }),

      removeAddon: (itemId, addonId) =>
        set((state) => {
          const item = state.items.find((i) => i.id === itemId);
          if (item) {
            const idx = item.addons.findIndex((a) => a.id === addonId);
            if (idx !== -1) item.addons.splice(idx, 1);
          }
        }),

      updateAddon: (itemId, addonId, field, value) =>
        set((state) => {
          const item = state.items.find((i) => i.id === itemId);
          if (item) {
            const addon = item.addons.find((a) => a.id === addonId);
            if (addon) {
              addon[field] = value;
            }
          }
        }),
    })),
    {
      name: 'wss-quote-storage-v2',
      version: 3,
      migrate: (persistedState: unknown, fromVersion: number) => {
        // Defensive migration: every schema change MUST land here so older
        // clients hydrate cleanly instead of silently corrupting state.
        // See ADR 0001 onwards for the schema-evolution context.
        if (fromVersion < 2) {
          return persistedState as QuoteState;
        }
        if (fromVersion === 2) {
          // v2 → v3: introduced depositPercent. Older workspaces lack the
          // field; default new sessions to 50% so the sidebar UI behaves.
          const s = persistedState as Partial<QuoteState>;
          return { ...s, depositPercent: s.depositPercent ?? 50 } as QuoteState;
        }
        if (fromVersion > 3) {
          // A persisted state from a future version landed in an older client.
          // Throwing away local workspace is safer than running pricing math
          // against a shape we don't recognise.
          console.warn(
            `[useQuoteStore] Persisted state version ${fromVersion} is newer than the code (v3). Discarding local workspace.`,
          );
          return undefined as unknown as QuoteState;
        }
        return persistedState as QuoteState;
      },
    },
  ),
);

import { calcItemTotal } from '../utils/pricing';

/**
 * The calculator's live totals. A thin wrapper: every figure is derived by the
 * pure `computeQuoteSummary` (Protected Zones #5 + #6), which is where the GST
 * company gate, the discount cap and the order charges are pinned by tests.
 * Do not re-derive any of them at a render site — read the fields here.
 */
export const useQuoteSummary = () => {
  const items = useQuoteStore((state) => state.items);
  const cbdSurcharge = useQuoteStore((state) => state.cbdSurcharge);
  const deliveryWaived = useQuoteStore((state) => state.deliveryWaived);
  const lumpSumDiscount = useQuoteStore((state) => state.lumpSumDiscount);
  const issuedCompany = useQuoteStore((state) => state.issuedCompany);

  const itemsTotal = items.reduce((sum, item) => sum + calcItemTotal(item), 0);

  return computeQuoteSummary({
    items,
    itemsTotal,
    cbdSurcharge,
    deliveryWaived,
    lumpSumDiscount,
    issuedCompany,
  });
};
